import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Line, Pie, Doughnut, Radar } from 'react-chartjs-2';
import GaugeChart from 'react-gauge-chart';
import ChartContainer from './ChartContainer';
import TimeRangeFilter, { Granularity } from './TimeRangeFilter';
import SkeletonClientItem from '../Clients/SkeletonClientItem';
import { getActiveColorTheme, ColorTheme, ChartTypeOverride } from './ColorCustomizer';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
  ChartDataLabels
);

// Disable datalabels globally — only enable per-chart for pie/doughnut
ChartJS.defaults.set('plugins.datalabels', { display: false });

export interface ChartDataInterface {
  chart_type: "bar" | "line" | "pie" | "doughnut" | "gauge" | "radar";
  datalabel_color?: string;
  chart_options?: {
    legend_status?: boolean;
    tooltip_text_color?: string;
    legend_position?: "top" | "left" | "bottom" | "right";
    chart_grid_lines?: boolean;
    tooltip_bg_color?: string;
    stacked?: boolean;
    show_x_axis?: boolean;
  };
  chart_title?: string;
  x_values: string[];
  y_values?: (number | string)[];
  color_codes?: string[];
  gradient?: string;
  datasets?: {
    label: string;
    data: number[];
    borderColor: string;
    fill: boolean;
    backgroundColor?: string;
    tension?: number;
  }[];
  apex_options?: any;
  apex_series?: number[];
}

/* ── Theme-aware color helpers ─────────────────────────────────────────── */

const getChartColors = (theme?: ColorTheme) => {
  const t = theme || getActiveColorTheme();
  return {
    primary: t.palette[0],
    secondary: t.palette[1],
    accent: {
      dark: t.palette[2],
      mid: t.palette[3],
      gray: t.palette[4],
      light: t.palette[5],
      muted: t.palette[6],
    },
    gradients: {
      dark: [t.palette[0], t.palette[1]],
      mid: [t.palette[2], t.palette[3]],
      light: [t.palette[4], t.palette[5]],
    },
  };
};

const getEnhancedColorPalette = (count: number, _providedColors?: string[], theme?: ColorTheme): string[] => {
  const t = theme || getActiveColorTheme();
  const result: string[] = [];
  for (let i = 0; i < count; i++) {
    result.push(t.palette[i % t.palette.length]);
  }
  return result;
};

/* ── Client-side time-series rebucketing ──────────────────────────────── */

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** Try to parse a label as a Date. Handles ISO "2026-02-15", "Feb 15", etc. */
const parseLabel = (label: string): Date | null => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(label)) return new Date(label + 'T00:00:00');
  if (/^\d{4}-\d{2}$/.test(label)) return new Date(label + '-01T00:00:00');
  const m = label.match(/^([A-Za-z]+)\s+(\d+)$/);
  if (m) {
    const monthIdx = SHORT_MONTHS.indexOf(m[1]);
    if (monthIdx >= 0) return new Date(new Date().getFullYear(), monthIdx, parseInt(m[2]));
  }
  return null;
};

const numVal = (v: number | string | undefined): number =>
  typeof v === 'number' ? v : parseFloat(v as string) || 0;

const formatLabel = (d: Date): string =>
  `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;

const rebucketChartItem = (
  item: ChartDataInterface,
  granularity: Granularity,
): ChartDataInterface => {
  const xVals = item.x_values;
  const yVals = item.y_values || [];
  const datasets = item.datasets;

  if (xVals.length === 0) return item;

  // 'days' — show each day with readable labels
  if (granularity === 'days') {
    const newX = xVals.map((lbl) => {
      const d = parseLabel(lbl);
      return d ? formatLabel(d) : lbl;
    });
    return { ...item, x_values: newX };
  }



  if (granularity === 'weeks') {
    const chunkSize = 7;
    const newX: string[] = [];
    const newY: (number | string)[] = [];
    for (let i = 0; i < xVals.length; i += chunkSize) {
      const chunk = xVals.slice(i, i + chunkSize);
      const vals = yVals.slice(i, i + chunkSize).map(numVal);
      const startD = parseLabel(chunk[0]);
      const endD = parseLabel(chunk[chunk.length - 1]);
      if (startD && endD) {
        newX.push(`${formatLabel(startD)} – ${formatLabel(endD)}`);
      } else {
        newX.push(chunk.length > 1 ? `${chunk[0]} – ${chunk[chunk.length - 1]}` : chunk[0]);
      }
      newY.push(vals.reduce((a, b) => a + b, 0));
    }
    let newDatasets = datasets;
    if (datasets && datasets.length > 0) {
      newDatasets = datasets.map((ds) => {
        const nd: number[] = [];
        for (let i = 0; i < ds.data.length; i += chunkSize) {
          nd.push(ds.data.slice(i, i + chunkSize).reduce((a, b) => a + b, 0));
        }
        return { ...ds, data: nd };
      });
    }
    return { ...item, x_values: newX, y_values: newY, datasets: newDatasets };
  }

  if (granularity === 'months') {
    const monthOrder: string[] = [];
    const monthY = new Map<string, number>();
    const monthDS = datasets?.map(() => new Map<string, number>());

    for (let i = 0; i < xVals.length; i++) {
      const d = parseLabel(xVals[i]);
      const month = d ? `${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}` : xVals[i].split(' ')[0];
      if (!monthY.has(month)) {
        monthOrder.push(month);
        monthY.set(month, 0);
        monthDS?.forEach((m) => m.set(month, 0));
      }
      monthY.set(month, (monthY.get(month) || 0) + numVal(yVals[i]));
      datasets?.forEach((ds, di) => {
        monthDS![di].set(month, (monthDS![di].get(month) || 0) + (ds.data[i] || 0));
      });
    }

    const newX = monthOrder;
    const newY = monthOrder.map((m) => monthY.get(m) || 0);
    let newDatasets = datasets;
    if (datasets && datasets.length > 0 && monthDS) {
      newDatasets = datasets.map((ds, di) => ({
        ...ds,
        data: monthOrder.map((m) => monthDS[di].get(m) || 0),
      }));
    }
    return { ...item, x_values: newX, y_values: newY, datasets: newDatasets };
  }

  return item;
};

const buildChartData = (item: ChartDataInterface, theme?: ColorTheme) => {
  const colors = getChartColors(theme);
  const isMultiDataset = item.chart_type === "line" && item.datasets;
  const isLineChart = item.chart_type === "line";
  const isBarChart = item.chart_type === "bar";
  if (isMultiDataset) {
    return {
      labels: item.x_values,
      datasets: item.datasets!.map((dataset, index) => {
        const shade = getEnhancedColorPalette(item.datasets!.length, undefined, theme)[index];
        return {
          ...dataset,
          borderColor: shade,
          backgroundColor: `${shade}12`,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointBackgroundColor: shade,
          pointBorderColor: '#fff',
          pointBorderWidth: 1.5,
          tension: 0.35,
        };
      }),
    };
  } else if (isLineChart) {
    const color = colors.primary;
    return {
      labels: item.x_values,
      datasets: [
        {
          label: item.chart_title ?? "Dataset",
          data: item.y_values!,
          borderColor: color,
          backgroundColor: `${color}0A`,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointBackgroundColor: color,
          pointBorderColor: '#fff',
          pointBorderWidth: 1.5,
          tension: 0.35,
          fill: true,
        },
      ],
    };
  } else if (isBarChart) {
    if (item.datasets && item.datasets.length > 0) {
      const palette = getEnhancedColorPalette(item.datasets.length, undefined, theme);
      return {
        labels: item.x_values,
        datasets: item.datasets.map((ds, i) => ({
          ...ds,
          barPercentage: 0.6,
          categoryPercentage: 0.7,
          backgroundColor: palette[i],
          borderColor: 'transparent',
          borderWidth: 0,
          borderRadius: 3,
          borderSkipped: false,
        })),
      };
    }
    const color = colors.primary;
    return {
      labels: item.x_values,
      datasets: [{
        label: item.chart_title ?? "Dataset",
        data: item.y_values!,
        backgroundColor: color,
        borderColor: 'transparent',
        borderWidth: 0,
        borderRadius: 3,
        borderSkipped: false,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      }],
    };
  } else {
    // Pie / Doughnut
    const pieColors = getEnhancedColorPalette(item.x_values.length, undefined, theme);
    return {
      labels: item.x_values,
      datasets: [
        {
          label: item.chart_title ?? "Dataset",
          data: item.y_values!,
          backgroundColor: pieColors,
          borderColor: '#ffffff',
          borderWidth: 2,
          borderRadius: 0,
          borderSkipped: false,
        },
      ],
    };
  }
};

const buildChartOptions = (item: ChartDataInterface) => {
  const {
    legend_status = true,
    legend_position = "top",
    tooltip_bg_color = "#111111",
    tooltip_text_color = "#FAFAFA",
    chart_grid_lines = true,
    stacked = false,
    show_x_axis,
  } = item.chart_options || {};

  const baseOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        display: legend_status,
        position: legend_position,
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          padding: 16,
          font: {
            size: 11,
            family: "'Inter', 'Helvetica Neue', sans-serif",
            weight: '500' as const,
          },
          color: '#888888',
          usePointStyle: true,
          pointStyle: 'rectRounded',
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: tooltip_bg_color,
        titleColor: tooltip_text_color,
        bodyColor: tooltip_text_color,
        borderColor: '#333333',
        borderWidth: 0,
        cornerRadius: 4,
        padding: { top: 8, bottom: 8, left: 12, right: 12 },
        titleFont: {
          size: 12,
          family: "'Inter', 'Helvetica Neue', sans-serif",
          weight: '600' as const,
        },
        bodyFont: {
          size: 12,
          family: "'Inter', 'Helvetica Neue', sans-serif",
        },
        displayColors: false,
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label || '';
            let value = context.parsed;
            if (typeof value === 'object' && value !== null) {
              value = value.y ?? value.x ?? 0;
            }
            return `${label}: ${typeof value === 'number' ? value.toLocaleString() : value}`;
          }
        }
      },
    },
    animation: {
      duration: 700,
      easing: 'easeOutQuart' as const,
    },
    elements: {
      point: {
        hoverBackgroundColor: '#111111',
        hoverBorderWidth: 2,
        hoverBorderColor: '#ffffff',
      },
      bar: {
        borderRadius: 3,
        borderSkipped: false,
      },
    },
  };

  if (item.chart_type === "pie" || item.chart_type === "doughnut") {
    return {
      ...baseOptions,
      cutout: '0%',
      layout: {
        padding: { top: 6, bottom: 20, left: 20, right: 20 },
      },
      plugins: {
        ...baseOptions.plugins,
        legend: {
          ...baseOptions.plugins.legend,
          position: 'top' as const,
          align: 'end' as const,
        },
        datalabels: {
          display: true,
          color: (ctx: any) => {
            const isDark = document.documentElement.classList.contains('dark');
            return isDark ? '#e5e7eb' : '#374151';
          },
          anchor: 'end' as const,
          align: 'end' as const,
          offset: 6,
          clip: false,
          font: {
            size: 10,
            weight: '600' as const,
            family: "'Inter', 'Helvetica Neue', sans-serif",
          },
          formatter: (value: number, ctx: any) => {
            const dataset = ctx.dataset.data as number[];
            const total = dataset.reduce((a: number, b: number) => a + b, 0);
            if (total === 0 || value === 0) return null;
            const pct = ((value / total) * 100).toFixed(0);
            if ((value / total) < 0.04) return null;
            return `${value} (${pct}%)`;
          },
        },
      },
    };
  }

  if (item.chart_type === "bar") {
    const hasMultiDatasets = !!(item.datasets && item.datasets.length > 0);
    const useStacked = stacked || hasMultiDatasets;
    return {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        legend: {
          ...baseOptions.plugins.legend,
          display: hasMultiDatasets,
        },
      },
      scales: {
        x: {
          stacked: useStacked,
          display: show_x_axis ?? true,
          grid: {
            display: false,
          },
          ticks: {
            color: '#999999',
            font: { size: 11, family: "'Inter', 'Helvetica Neue', sans-serif" },
            maxRotation: 0,
          },
          border: { display: false },
        },
        y: {
          stacked: useStacked,
          beginAtZero: true,
          grid: {
            display: chart_grid_lines,
            color: '#F0F0F0',
            drawBorder: false,
          },
          ticks: {
            color: '#999999',
            font: { size: 11, family: "'Inter', 'Helvetica Neue', sans-serif" },
            callback: (v: any) => (typeof v === 'number' ? v.toLocaleString() : v),
            padding: 8,
          },
          border: { display: false },
        },
      },
    };
  }

  return {
    ...baseOptions,
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#999999',
          font: { size: 11, family: "'Inter', 'Helvetica Neue', sans-serif" },
          maxRotation: 0,
        },
        border: { display: false },
      },
      y: {
        min: 0,
        grid: {
          display: chart_grid_lines,
          color: '#F0F0F0',
          drawBorder: false,
        },
        ticks: {
          color: '#999999',
          font: { size: 11, family: "'Inter', 'Helvetica Neue', sans-serif" },
          callback: (v: any) => (typeof v === 'number' ? v.toLocaleString() : v),
          padding: 8,
        },
        border: { display: false },
      },
    },
  };
};

const renderChart = (chartItem: ChartDataInterface, theme?: ColorTheme) => {
  const chartData = buildChartData(chartItem, theme);
  const chartOptions = buildChartOptions(chartItem);

  switch (chartItem.chart_type) {
    case "bar":
      return (
        <div className="h-64">
          <Bar data={chartData} options={chartOptions} />
        </div>
      );
    case "line":
      return (
        <div className="h-64">
          <Line data={chartData} options={chartOptions} />
        </div>
      );
    case "pie":
    case "doughnut":
      return (
        <div className="h-64">
          <Pie data={chartData} options={chartOptions} />
        </div>
      );
    case "radar":
      return (
        <div className="h-64">
          <Radar data={chartData} options={chartOptions} />
        </div>
      );
    case "gauge": {
      const value = chartItem.apex_series?.[0] ?? 0;
      const tc = getChartColors(theme);

      return (
        <div className="h-64 flex flex-col items-center justify-center">
          <div className="w-full max-w-xs">
            <GaugeChart
              id={`gauge-${Math.random()}`}
              nrOfLevels={20}
              percent={value / 100}
              arcsLength={[1]}
              colors={[tc.secondary]}
              textColor={tc.primary}
              formatTextValue={() => `${value}`}
              needleColor={tc.accent.dark}
              needleBaseColor={tc.accent.gray}
              arcPadding={0.02}
              animate={true}
              style={{ width: "100%", height: "auto" }}
            />
          </div>
          <div className="mt-2 text-sm font-medium text-gray-700">
            {chartItem.chart_title}
          </div>
        </div>
      );
    }
    default:
      return (
        <div className="h-64">
          <Bar data={chartData} options={chartOptions} />
        </div>
      );
  }
};

interface EnhancedAnalyticsChartsProps {
  chartsData: ChartDataInterface[];
  isLoading?: boolean;
  seperatedChart?: boolean;
  /** Active color theme key (e.g. 'blue', 'purple') */
  colorTheme?: string;
  /** Override chart type for bar/line charts */
  chartTypeOverride?: ChartTypeOverride;
}

/** Detect "over time" charts that should show the granularity filter */
const isTimeSeriesChart = (item: ChartDataInterface): boolean => {
  const title = (item.chart_title || '').toLowerCase();
  return (
    (item.chart_type === 'bar' || item.chart_type === 'line') &&
    (title.includes('over time') || title.includes('trend'))
  );
};

const EnhancedAnalyticsCharts: React.FC<EnhancedAnalyticsChartsProps> = ({
  chartsData,
  isLoading = false,
  seperatedChart = false,
  colorTheme,
  chartTypeOverride = 'default',
}) => {
  const [animatedCharts, setAnimatedCharts] = useState<boolean[]>([]);
  const [granularities, setGranularities] = useState<Record<number, Granularity>>({});

  const theme = useMemo(() => getActiveColorTheme(colorTheme), [colorTheme]);

  useEffect(() => {
    const timers = chartsData.map((_, index) =>
      setTimeout(() => {
        setAnimatedCharts(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, index * 150)
    );
    return () => timers.forEach(clearTimeout);
  }, [chartsData]);

  const handleGranularityChange = useCallback((index: number, g: Granularity) => {
    setGranularities(prev => ({ ...prev, [index]: g }));
    // Purely local — no API refetch, just rebuckets the chart data client-side
  }, []);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        <SkeletonClientItem viewMode="grid" count={6} />
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 ${seperatedChart ? 'lg:grid-cols-2' : 'lg:grid-cols-2 xl:grid-cols-3'} gap-6`}>
      {chartsData.map((chartItem, index) => {
        const showFilter = isTimeSeriesChart(chartItem);
        const gran = granularities[index];
        // Apply client-side rebucketing when a non-default granularity is selected
        // Always format labels (ISO → "Feb 18"); rebucket for weeks/months
        const displayItem = rebucketChartItem(chartItem, gran || 'days');

        // Apply chart type override (only for bar/line charts)
        const finalItem = (chartTypeOverride !== 'default' && (displayItem.chart_type === 'bar' || displayItem.chart_type === 'line'))
          ? { ...displayItem, chart_type: chartTypeOverride as 'bar' | 'line' }
          : displayItem;
        return (
          <div
            key={index}
            className={`transition-all duration-500 ${animatedCharts[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <ChartContainer
              title={chartItem.chart_title || `Chart ${index + 1}`}
              subtitle={
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                  style={{
                    backgroundColor: `${theme.primary}15`,
                    color: theme.primary,
                    border: `1px solid ${theme.primary}30`,
                  }}
                >
                  {(finalItem.chart_type === 'doughnut' ? 'Pie' : finalItem.chart_type.charAt(0).toUpperCase() + finalItem.chart_type.slice(1))} Chart
                </span>
              }
              topRightElement={
                showFilter ? (
                  <TimeRangeFilter
                    value={granularities[index] || 'days'}
                    onChange={(g) => handleGranularityChange(index, g)}
                  />
                ) : undefined
              }
            >
              {renderChart(finalItem, theme)}
            </ChartContainer>
          </div>
        );
      })}
    </div>
  );
};

export default EnhancedAnalyticsCharts;